<script lang="ts">
  import FileItem from './FileItem.svelte';
  import { Icon } from '$shared/components';
  import type { Document } from '$shared/models/Document';
import { IconType } from '$shared/constants/IconType';

  interface Props {
    disabled?: boolean;
    selectedItems?: string[];
    doc: Document;
    onclick?: (doc: Document) => void;
    onopenfile?: (doc: Document) => void;
  }

  let { disabled = false, selectedItems = [], doc, onclick, onopenfile }: Props = $props();

  let open = $state(true);

  let icon = $derived((doc?.isFolder && 'icon-folder') || 'icon-file');
  let arrowIcon = $derived((open && 'icon-chevron-down') || 'icon-chevron-right');

  const onClickSelectFile = (doc: Document): void => {
    onclick?.(doc);
  };

  const onClickToggle = (): void => {
    if (!doc?.isFolder) return;
    open = !open;
  };

  const onClickFileLink = (): void => {
    onopenfile?.(doc);
  };
</script>

<button
  type="button"
  class="list-i"
  data-cy={doc?.name}
  {disabled}
  class:active={selectedItems.includes(doc?.id)}
  onclick={() => {
    onClickSelectFile(doc);
  }}
>
  {#if !doc?.isFolder && !disabled}
    <div class="icon" class:show={selectedItems.includes(doc?.file?.id)}>
      <Icon value="icon-check" />
    </div>
  {/if}
  {#if doc?.isFolder}
    <div class="arrow-icon">
      <Icon name="toggleIcon" type={IconType.Button} value={arrowIcon} onclick={onClickToggle} />
    </div>
  {/if}
  <div class="file-container">
    <div class="file-label">
      <div class="file-info">
        <div class="file-icon">
          <Icon value={icon} />
        </div>
        <div class="label">{doc?.name}</div>
      </div>
      {#if !doc?.isFolder && !disabled}
        <div class="link">
          <Icon
            disabled={!doc?.isPreviewSupportedFileType}
            value="icon-external-link"
            type={IconType.Button}
            onclick={onClickFileLink}
          />
        </div>
      {/if}
    </div>
    {#if doc?.additionalInfo}
      <div class="additional-text">{doc.additionalInfo}</div>
    {/if}
  </div>
</button>
{#if open && doc?.children}
  <div class="children" class:disabled>
    {#each doc?.children as child}
      <FileItem doc={child} {selectedItems} {disabled} {onclick} {onopenfile} />
    {/each}
  </div>
{/if}

<style>
  .list-i {
    width: 100%;
    padding: 16px 8px;
    display: flex;
    align-items: center;
  }
  .list-i:disabled {
    opacity: 0.5;
    color: var(--black);
    cursor: not-allowed;
    padding-left: 16px;
  }
  .list-i .label {
    text-align: left;
  }
  .list-i .icon,
  .list-i .file-icon {
    flex-basis: 24px;
    flex-grow: 0;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    opacity: 0;
  }
  .list-i .file-icon {
    opacity: 1;
    margin-right: 4px;
  }
  .list-i .icon.show {
    opacity: 1;
  }
  .list-i:hover,
  .list-i.active {
    background-color: var(--light-blue);
  }

  .children {
    padding-left: 32px;
  }
  .children.disabled {
    padding-left: 16px;
  }
  .arrow-icon {
    margin-right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .file-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: flex-start;
  }
  .file-label {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }
  .file-info {
    display: inline-flex;
    flex-basis: 90%;
  }
  .additional-text {
    font-style: italic;
    color: var(--grey-dark);
    font-size: 12px;
    margin-top: 2px;
  }
</style>
